import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { BackTop, useScrollTop, Breadcrumb } from "@/components/feature/UI";
import { channels } from "@/mocks/categories";
import { siteName, useLdJson, useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dagmnfbep053ijub2e2g";

interface FormState {
  status: "idle" | "submitting" | "success" | "error";
  error: string;
}

export default function ContactPage() {
  useScrollTop();
  const [state, setState] = useState<FormState>({ status: "idle", error: "" });

  useSeo({
    title: `投稿爆料与联系我们 - ${siteName}`,
    description: `向${siteName}匿名投稿爆料：新晋网红黑料、练习生爆料、新人演员翻车线索均可提交。保护隐私，编辑部核实后谨慎发布。`,
    keywords: "每日新瓜,投稿爆料,匿名投稿,娱乐线索,联系我们",
  });

  useLdJson(
    [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "投稿爆料与联系",
        description: "提交娱乐资讯线索的投稿表单。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin + "/" },
          { "@type": "ListItem", position: 2, name: "投稿与联系" },
        ],
      },
    ],
    "contact-ld"
  );

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const hp = (fd.get("company_alt") || "").toString().trim();
    if (hp) {
      setState({ status: "success", error: "" });
      return;
    }
    fd.delete("company_alt");

    setState({ status: "submitting", error: "" });
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as URLSearchParams).toString(),
      });
      const text = await res.text();
      let code = "";
      let msg = "";
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code ?? "";
        msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          "";
      } catch {
        code = res.ok ? "OK" : "";
      }
      if (res.ok && code === "OK") {
        form.reset();
        setState({ status: "success", error: "" });
      } else {
        setState({ status: "error", error: msg || "提交失败，请稍后再试或通过邮箱联系我们。" });
      }
    } catch {
      setState({ status: "error", error: "网络异常，请稍后再试或通过邮箱联系我们。" });
    }
  };

  const inputCls =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <>
      <Header />
      <main>
        <section className="border-b border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6">
            <Breadcrumb items={[{ label: "投稿与联系" }]} />
            <div className="mt-6">
              <span className="font-label text-xs font-semibold uppercase tracking-[0.3em] text-primary-600">
                SUBMIT A TIP · 投稿爆料
              </span>
              <h1 className="font-display mt-3 text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                你有线索，我们有耐心拆解
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
                无论你是行业从业者还是眼尖的网友，欢迎提交你掌握的新瓜线索。支持匿名投稿，编辑部会认真核实、谨慎发布。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-[1200px] grid-cols-1 gap-8 px-4 py-10 md:px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-display text-lg font-bold text-foreground-950">投稿须知</h2>
            <ul className="mt-4 flex flex-col gap-3">
              {[
                { i: "ri-mail-send-line", t: "信息越全，越好核实", d: "提供事件经过、时间地点与可公开的证据链接，能大幅提高采用率。" },
                { i: "ri-eye-off-line", t: "全程匿名保护", d: "支持匿名投稿，涉及个人信息的线索会做脱敏，不会未经同意公开你的身份。" },
                { i: "ri-alert-line", t: "拒绝违规内容", d: "请勿提交虚假、诽谤、偷拍私密等违法内容，编辑部有权不予发布。" },
                { i: "ri-time-line", t: "审稿需要时间", d: "线索核实需要时间沉淀，若内容已由其他渠道发布，可能不再单独跟进。" },
              ].map((it) => (
                <li key={it.t} className="flex items-start gap-3 rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={it.i}></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{it.t}</p>
                    <p className="mt-1 text-xs leading-5 text-foreground-500">{it.d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-lg bg-background-100/70 p-5">
              <h3 className="font-display flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-headphone-line text-primary-500"></i>
                其他联系方式
              </h3>
              <div className="mt-3 flex flex-col gap-2 text-sm text-foreground-600">
                <p className="flex items-center gap-2"><i className="ri-mail-line text-foreground-400"></i> dailygossip@example.com</p>
                <p className="flex items-center gap-2"><i className="ri-wechat-line text-foreground-400"></i> 公众号：每日新瓜</p>
              </div>
              <Link to="/about" className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700">
                了解本站立场 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="font-display text-lg font-bold text-foreground-950">提交你的新瓜线索</h2>
              <p className="mt-1 text-xs text-foreground-500">
                带 * 为必填项。事件描述请控制在 500 字以内。
              </p>

              <form
                id="contact-form"
                data-readdy-form="每日新瓜投稿爆料联系表单"
                onSubmit={onSubmit}
                noValidate
                className="mt-5 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      你的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input id="name" name="name" type="text" required placeholder="笔名 / 昵称（可匿名）" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      联系邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input id="email" name="email" type="email" required placeholder="用于反馈核实进度" className={inputCls} autoComplete="email" />
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    归属栏目 <span className="text-primary-500">*</span>
                  </label>
                  <select id="category" name="category" required className={inputCls} defaultValue="">
                    <option value="" disabled>
                      请选择线索所属栏目
                    </option>
                    {channels.map((c) => (
                      <option key={c.key} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                    <option value="其他">其他 / 不确定</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    线索标题 <span className="text-primary-500">*</span>
                  </label>
                  <input id="subject" name="subject" type="text" required placeholder="一句话概括你的线索，例如「某新晋主播疑似买量起号」" className={inputCls} />
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    事件描述 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="尽量说明事件经过、涉及的人物（可用化名）、发生时间地点，以及你掌握的证据……"
                    className={`${inputCls} resize-y`}
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>

                <div>
                  <label htmlFor="source" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    可公开的证据 / 链接 <span className="text-xs text-foreground-400">（选填）</span>
                  </label>
                  <input id="source" name="source" type="text" placeholder="粘贴截图外链、原文链接等（非必填）" className={inputCls} />
                </div>

                <div className="form-hp-holder" aria-hidden="true">
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                </div>

                {state.status === "error" && (
                  <p className="flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-900">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {state.error}
                  </p>
                )}
                {state.status === "success" && (
                  <p className="flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    投稿已收到，感谢你的信任。编辑部会尽快核实，如有需要将通过邮箱与你联系。
                  </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={state.status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={state.status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {state.status === "submitting" ? "提交中…" : "提交线索"}
                  </button>
                  <p className="text-xs text-foreground-400">提交即代表你同意内容仅用于编辑部核实，不会未经同意公开。</p>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}